import { Env, HospitalityGroup } from "@mmh/types"

export const apiBaseUrl = import.meta.env.PUBLIC_API_BASE_URL || "http://localhost:4000"
export const env = import.meta.env.PUBLIC_ENV as Env || "dev"
export const hospitalityGroup: HospitalityGroup = {
    name: "memento-mori-hospitality",
    tagline: "Hand-crafted Cuisine and Gracious Hospitality",
    description: "Memento Mori Hospitality is a family of restaurants located in Southern California that offers hand-crafted cuisine and gracious hospitality. Our restaurants include Ospi, Jemma, and Jame. Our restaurant locations include Venice, Brentwood, Costa Mesa, El Segundo, Hollywood, Pacific Palisades, and Montecito.",
    footerTagline: "Family of Restaurants",
    contact: {
        address: "241 Main Street, El Segundo, CA 90245",
        email: "hello@mmh.co",
        phone: "(310) 648-8554",
        website: "https://mmh.co"
    },
    parentCompany: {
        name: "Memento Mori Hospitality Group",
        dba: "Memento Mori Hospitality",
        address: "241 Main Street, El Segundo, CA 90245",
        email: "hello@mmh.co",
        phone: "(310) 648-8554",
        jurisdiction: "Los Angeles, County of Los Angeles, California",
        website: "https://mmh.co"
    },
    images: {
        navLogoLink: "/img/shared/group/group-badge.svg",
        footerLogo: "/img/shared/group/group-badge.svg"
    },
    external: {
        careers: "https://recruitingbypaycor.com/career/CareerHome.action?clientId=8a7883c699734e1b0199bc0c93de3f04",
        klaviyo: {
            company_id: "Ywubv6",
            list_id: "XqZeFx"
        },
        hubspot: {
            contactUs: {
                formId: "c6b9febc-d87e-481d-8f4e-01b49f493bdf",
                portalId: "22788501"
            }
        }
    },
    admins: [
        {
            name: "Tara Kelly",
            email: "tara@mmh.co",
            avatar: "/avatars/shadcn.jpg"
        }
    ],
    pages: {
        home: {
            hero: {
                images: {
                    background: "/img/hero.jpg",
                    logo: "/img/multi-brand-logo.svg"
                }
            },
            gallery: {
                images: [
                    {
                        path: "/img/gallery/capellini-pasta.jpg",
                        alt: "Capellini Pasta"
                    },
                    {
                        path: "/img/gallery/ospi-costa-mesa-bar.jpg",
                        alt: "The bar at Ospi Costa Mesa"
                    },
                    {
                        path: "/img/gallery/vodka-pizza.jpg",
                        alt: "Vodka Pizza"
                    },
                    {
                        path: "/img/gallery/ospi-brentwood-interior.jpg",
                        alt: "Ospi Brentwood"
                    },
                    {
                        path: "/img/gallery/guests-in-john-thomas-bar.jpg",
                        alt: "A private event at John Thomas Bar at Jame"
                    },
                    {
                        path: "/img/gallery/cocktail-assortment-jame.jpg",
                        alt: "Assortment of cocktails at Jame"
                    }
                ]
            },
            newsletter: {
                image: "/img/home/newsletter.jpg",
                alt: "Hand holding a bowl of pasta in front of wall of tomato soup cans inside Opsi Venice restaurant",
                title: "Sign Up for Our Email Newsletter",
                content: "Stay up to date on all things Memento Mori Hospitality.",
                cta: "Sign Up"
            },
            givingBack: {
                title: "Giving Back",
                content: "For each meal purchased at one of our restaurants (Ospi, Jemma, Jame), Memento Mori Hospitality donates a meal to someone in need, in partnership with Feeding America and No Kid Hungry. <br><br>With your help, we are proud to give a meal for a meal.",
                secondaryTitle: "Donate and Learn More",
                images: [
                    {
                        name: "Feeding America",
                        url: "https://www.feedingamerica.org",
                        src: "/img/feeding-america.avif",
                        alt: "Feeding America logo"
                    },
                    {
                        name: "No Kid Hungry",
                        url: "https://www.nokidhungry.org",
                        src: "/img/no-kid-hungry.avif",
                        alt: "No Kid Hungry logo"
                    }
                ]
            }
        },
        ourStory: {},
        ourRestaurants: {},
        careers: {
            content: "At Memento Mori Hospitality, our mission is to empower and inspire our employees, so that together we can do what we love: create meaningful experiences for our guests. We strive to give our employees the same level of hospitality that we provide every guest who walks through our doors. We will support you as you polish your strengths, and give you a place to shine when you are ready. In return, we ask for excellence, integrity, and an entrepreneurial spirit. Our vision is to be the most rewarding employer in hospitality by encouraging our team to be creative, scrappy, and passionate.",
            staff: {
                id: "834377028",
                anchorId: "formAnchor834377028",
                anchorName: "form834377028",
                key: "https://fs7.formsite.com/res/showFormEmbed?EParam=vIGmKZCQ4HpdIEgPGBD6jsd_CVfYhMrF&834377028"
            },
            management: {
                id: "641864898",
                anchorId: "formAnchor641864898",
                anchorName: "form641864898",
                key: "https://fs7.formsite.com/res/showFormEmbed?EParam=vIGmKZCQ4HpdIEgPGBD6jjN7iSoh21FL&641864898"
            }
        },
        contactUs: {
            id: "1256858366",
            anchorId: "formAnchor1256858366",
            anchorName: "form1256858366",
            key: "https://fs7.formsite.com/res/showFormEmbed?EParam=vIGmKZCQ4HpdIEgPGBD6jhjGupL6arfd&1256858366"
        }
    },
    concepts: [
        {
            name: "ospi",
            about: "Ospi is short for ospitante - meaning \"host\" in Italian. The sophomore restaurant of Jackson & Melissa Kalb, Ospi is an elevated celebration of a classic Brooklyn red sauce joint. Part modern Italian, part classic Italian, Ospi is our version of a casual lunch trattoria, familial dinner restaurant, and recovery brunch spot on the weekends. Welcome to our dinner party.",
            logoUrl: "/img/ospi-logo-gold.svg",
            thumbUrl: "/global/hospitality-group/ospi.jpg",
            website: "https://ospi.com",
            cuisine: "italian",
            images: [
                "/img/home/feed-me-pasta-brentwood.jpg",
                "/img/home/butter-chicken.jpeg"
            ],
            locations: [
                {
                    name: "venice",
                    url: "https://ospi.com/venice",
                    image: "/img/our-story/ospi-venice-exterior.jpg",
                    about: [
                        "Ospi is short for ospitante - meaning \"host\" in Italian. The sophomore restaurant of Jackson & Melissa Kalb, Ospi is an elevated celebration of a classic Brooklyn red sauce joint with a counterculture twist influenced by its surroundings in Venice. Part modern Italian, part classic Italian, Ospi is our version of a casual lunch trattoria, familial dinner restaurant, and recovery brunch spot on the weekends. Welcome to our dinner party."
                    ],
                    address: [
                        "2025 Pacific Ave",
                        "Venice, CA 90291"
                    ],
                    county: "Los Angeles",
                    onlineOrdering: "https://order.toasttab.com/online/ospi-venice"
                },
                {
                    name: "brentwood",
                    url: "https://ospi.com/brentwood",
                    image: "/img/brentwood.png",
                    about: [
                        "It's time to party at Ospi. Chef Jackson Kalb wants to bring the vibe of a dinner party seven nights (and days) a week from the beloved Venice concept to Brentwood. Alongside our renowned, painstakingly handmade pasta, we now offer our signature pizza romana tonda, endless cheese pulls, and a variety of specialty spritzes during weekend brunch."
                    ],
                    address: [
                        "11677 San Vicente Blvd #200",
                        "Los Angeles, CA 90049"
                    ],
                    county: "Los Angeles",
                    onlineOrdering: "https://order.toasttab.com/online/ospi-brentwood"
                },
                {
                    name: "costa-mesa",
                    url: "https://ospi.com/costamesa",
                    image: "/img/costa-mesa.png",
                    about: [
                        "Southern Italy meets coastal California at Ospi Costa Mesa. From Top Chef alumnus Jackson Kalb, Ospi redefines traditional Nonna's cooking with Michelin technique and a few twists. Ospi should feel like your favorite lunch, brunch, or dinner party 7 days a week - it's a vibe."
                    ],
                    address: [
                        "234 E 17th Street, Suite 100",
                        "Costa Mesa, CA 92627"
                    ],
                    county: "Orange County",
                    onlineOrdering: "https://order.toasttab.com/online/ospi-costa-mesa"
                },
                {
                    name: "montecito",
                    url: "https://ospi.com/montecito",
                    image: "/img/our-restaurants/montecito-location.jpg",
                    about: [
                        "Cal-Italian coastal elegance never tasted so good. Ospi Montecito at The Post is Chef Jackson Kalb’s newest destination for your favorite dinner party.",
                        "Building on the success of Ospi Venice, Brentwood, and Costa Mesa, we’re serving up our must-try house-made pastas and crispy Romana-tonda pizzas, plus our first-ever breakfast, and an exclusive wood-grilled selection featuring locally sourced meat, seafood, and seasonal vegetables from our top-quality vendors. Thoughtfully curated wines and inventive craft cocktails make it the perfect place for a dining destination any time of day.",
                        "Ospi Montecito reimagines Italian cuisine with modern sophistication, delivering a dining experience as effortlessly refined as the community it calls home."
                    ],
                    address: [
                        "1801 E Cabrillo Blvd",
                        "Montecito, CA 93108"
                    ],
                    county: "Santa Barbara",
                    onlineOrdering: "https://order.toasttab.com/online/ospi-montecito"
                },
                {
                    name: "Dallas",
                    url: "https://ospi.com/dallas",
                    image: "/img/our-restaurants/dallas.jpg",
                    about: [
                        "Ospi brings its signature modern Southern-Italian cooking to the Dallas Design District, pairing genuine hospitality with the vibrant energy of one of Dallas’s premier dining neighborhoods. Chef Jackson Kalb (of Top Chef ) originally founded the concept in Venice, California, with an irresistible vodka sauce and a menu centered around handmade pastas, crispy romana-tonda pizzas, and thoughtfully sourced ingredients. Designed for lively dinners, leisurely lunches, and brunch with friends, Ospi Dallas is a neighborhood restaurant with the feel of your favorite dinner party at a friend's home."
                    ],
                    address: [
                        "1621 Oak Lawn Ave",
                        "Dallas, TX 75207"
                    ],
                    county: "Dallas"
                    // onlineOrdering: "https://order.toasttab.com/online/ospi-dallas"
                }
            ]
        },
        {
            name: "jemma",
            about: "Jemma pays homage to old-school Italian-American joints with a touch of modern California flair. Our pizzas are made with an organic flour dough that took over four years to perfect and Lodi, CA tomatoes that feature the perfect blend of sweetness and acidity. Experience the classics like spicy rigatoni and spaghetti & meatballs, alongside craft cocktails. ",
            logoUrl: "/img/jemma-logo-gold.svg",
            thumbUrl: "/global/hospitality-group/jemma.jpg",
            website: "https://jemmarestaurants.com",
            cuisine: "italian",
            images: [
                "/img/home/jemma-interior.jpg",
                "/img/home/jemma-pizza-assortment.jpg"
            ],
            locations: [
                {
                    name: "hollywood",
                    url: "https://jemmarestaurants.com/hollywood",
                    image: "/img/hollywood.jpg",
                    about: [
                        "Jemma pays homage to old-school Italian-American joints with a touch of modern California flair. Our pizzas are made with an organic flour dough that took over four years to perfect and Lodi, CA tomatoes that feature the perfect blend of sweetness and acidity. At Hollywood & Vine, experience the classics like spicy rigatoni and spaghetti & meatballs, alongside craft cocktails. "
                    ],
                    address: [
                        "1717 Vine Street",
                        "Los Angeles, CA 90028"
                    ],
                    county: "Los Angeles",
                    onlineOrdering: "https://order.toasttab.com/online/jemma-hollywood"
                }
            ]
        },
        {
            name: "jame-enoteca",
            about: "Jame is the first restaurant of husband and wife restaurateur duo, Jackson and Melissa Kalb. Located in El Segundo, Jame is a true neighborhood restaurant and bar. Chef Jackson has created an offering of hand-rolled pastas, sandwiches, and salads - all made with local, market-driven ingredients. At Jame, you're part of the family.",
            logoUrl: "/img/jame-logo-gold.svg",
            thumbUrl: "/global/hospitality-group/jame-enoteca.jpg",
            website: "https://eatjame.com",
            cuisine: "italian",
            images: [
                "/img/home/jame-john-thomas-bar.jpg",
                "/img/home/pasta-assortment.jpeg"
            ],
            locations: [
                {
                    name: "jame",
                    url: "https://eatjame.com",
                    image: "/img/jame.jpg",
                    about: [
                        "Jame is the first restaurant of husband and wife restaurateur duo, Jackson and Melissa Kalb. Located in El Segundo, Jame is a true neighborhood restaurant and bar. Chef Jackson has created an offering of hand-rolled pastas, sandwiches, and salads - all made with local, market-driven ingredients. At Jame, you're part of the family."
                    ],
                    address: [
                        "241 Main Street",
                        "El Segundo, CA 90245"
                    ],
                    county: "Los Angeles",
                    onlineOrdering: "https://order.toasttab.com/online/jame"
                }
            ]
        },
        {
            name: "¡jaime!-taqueria",
            about: "Located just down the street from Jackson’s very first restaurant Jame Enoteca, this vibrant new spot will feature a bold and satisfying menu of hand-rolled tortillas, tacos, burritos, signature variations of guac, and inventive margaritas – all crafted with high-quality ingredients and a chef-driven touch. Designed to be both lively and welcoming, ¡Jaime! Taqueria is a fun, inviting local spot that brings the vibrant feel of California-Mexican to our El Segundo community.",
            logoUrl: "/img/jaime-logo-square-gold.png",
            thumbUrl: "/global/hospitality-group/¡jaime!-taqueria.jpg",
            website: "https://jaimetaqueria.com",
            images: [
                "/img/home/jaime-taqueria-sign.jpg",
                "/img/home/jaime-taqueria-food.jpg"
            ],
            cuisine: "mexican",
            locations: [
                {
                    name: "¡jaime!",
                    url: "https://jaimetaqueria.com",
                    image: "/img/our-restaurants/jaime-taqueria-sign.jpg",
                    about: [
                        "Located just down the street from Jackson’s very first restaurant Jame Enoteca, this vibrant new spot will feature a bold and satisfying menu of hand-rolled tortillas, tacos, burritos, signature variations of guac, and inventive margaritas – all crafted with high-quality ingredients and a chef-driven touch. Designed to be both lively and welcoming, ¡Jaime! Taqueria is a fun, inviting local spot that brings the vibrant feel of California-Mexican to our El Segundo community."
                    ],
                    address: [
                        "450 Main Street",
                        "El Segundo CA 90245"
                    ],
                    county: "Los Angeles",
                    onlineOrdering: "https://order.toasttab.com/online/jaime-taqueria-el-segundo"
                }
            ]
        }
    ]
} as const
